import { Badge, Button, Flex, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { Icons } from '@texas/components/shared/Icons';
import { TrackerWrapper } from '@texas/components/shared/tracker/TrackerWrapper';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { InquiryRowActions } from './InquiryRowActions';
import { InquiryStatus } from './InquiryStatus';
import {
  InquiryVersionSlot,
  InquiryWithContext,
} from '@texas/api/endpoints/inquiryApi';
import { useCurrencies } from '@texas/hooks/metadata/useCurrencies';
import { TFunction } from 'i18next';

export interface InquiryListRow {
  inquiry: InquiryWithContext | null;
  slot: InquiryVersionSlot;
}

interface Props {
  t: TFunction;
  mode: 'matrix' | 'non-matrix';
  tierColumns: number[];
  displayCurrencyId: number | null;
  convertCurrencyValue: ReturnType<
    typeof useCurrencies
  >['convertCurrencyValue'];
  findCurrency: ReturnType<typeof useCurrencies>['findCurrency'];
  onActivateInquiry: (compositionGroupPurchaseInfoId: number) => void;
}

const columnHelper = createColumnHelper<InquiryListRow>();

export function inquiryColumns({
  t,
  mode,
  tierColumns,
  displayCurrencyId,
  convertCurrencyValue,
  findCurrency,
  onActivateInquiry,
}: Props) {
  const sharedColumns = [
    columnHelper.display({
      id: 'inquiry',
      header: t('inquiry.inquiry'),
      size: 150,
      minSize: 140,
      cell: ({ row: { original } }) => {
        const { inquiry } = original;
        if (!inquiry) {
          return (
            <Text fontFamily="barlow" fontSize="15px" fontWeight="semibold">
              {t('inquiry.noInquiry')}
            </Text>
          );
        }
        return (
          <TrackerWrapper entityId={inquiry.id} entityType={EntityType.Inquiry}>
            <Flex direction="column" gap={1.5}>
              <Flex align="center" gap={1.5}>
                <Text fontFamily="barlow" fontSize="16px" fontWeight="semibold">
                  <AngularNavLink
                    to="variant.inquiry.detail"
                    navobj={{
                      compositionGroupId: original.slot.compositionGroupId,
                      inquiryId: inquiry.id,
                    }}
                    onClick={() => undefined}
                  >
                    {inquiry.formattedInquiryNumber}
                  </AngularNavLink>
                </Text>
              </Flex>
              <Flex align="center" gap={1.5} wrap="wrap">
                <InquiryStatus inquiry={inquiry} />
                {inquiry.isMain && (
                  <Badge
                    display="inline-flex"
                    alignItems="center"
                    gap={1}
                    flexShrink={0}
                    bg="inquiryOverview.activeBadgeBackground"
                    color="inquiryOverview.activeBadgeText"
                    borderRadius="sm"
                    fontSize="10px"
                    textTransform="none"
                  >
                    <Icons.Flag boxSize={3} flexShrink={0} />
                    {t('inquiry.main')}
                  </Badge>
                )}
              </Flex>
            </Flex>
          </TrackerWrapper>
        );
      },
    }),
    columnHelper.display({
      id: 'supplier',
      header: t('suppliers.supplier'),
      size: 190,
      minSize: 160,
      cell: ({ row: { original } }) => (
        <Flex direction="column" gap={0.5}>
          <Text fontSize="14px" fontWeight="semibold">
            {original.slot.supplierName}
          </Text>
          {original.inquiry && (
            <Text color="entityDirectory.mutedText" fontSize="12px">
              {original.inquiry.supplierContactName ??
                original.inquiry.supplierContactEmail ??
                '—'}
            </Text>
          )}
        </Flex>
      ),
    }),
    columnHelper.display({
      id: 'version',
      header: t('inquiry.optionVersion'),
      size: 140,
      minSize: 125,
      cell: ({ row: { original } }) => {
        const { inquiry, slot } = original;
        return (
          <Flex direction="column" gap={0.5}>
            <Text>
              {`${
                slot.basedOnGroupVersion
                  ? `O${slot.basedOnGroupVersion} · `
                  : ''
              }V${slot.version}`}
            </Text>
            {slot.basedOnGroupName && (
              <Text color="entityDirectory.mutedText" fontSize="11px">
                {slot.basedOnGroupName}
              </Text>
            )}
            {mode === 'non-matrix' && slot.mainProductApprovalNumber && (
              <Badge alignSelf="flex-start" fontSize="10px">
                {slot.mainProductApprovalNumber}
              </Badge>
            )}
            {mode === 'matrix' && inquiry && inquiry.missingCellCount > 0 && (
              <Badge colorScheme="orange" fontSize="10px">
                {t('inquiry.incompleteCells', {
                  count: inquiry.missingCellCount,
                })}
              </Badge>
            )}
          </Flex>
        );
      },
    }),
    columnHelper.display({
      id: 'branch',
      header: t('general.branch'),
      size: 80,
      minSize: 70,
      cell: ({ row: { original } }) => (
        <Text fontSize="12px" fontWeight="semibold">
          {original.slot.branchIdentifier}
        </Text>
      ),
    }),
    columnHelper.display({
      id: 'owner',
      header: t('inquiry.internalOwner'),
      size: 140,
      minSize: 125,
      cell: ({ row: { original } }) => {
        const { inquiry } = original;
        if (!inquiry) return null;
        return (
          <Flex direction="column" gap={0.5}>
            <Text fontWeight="medium">{inquiry.internalOwnerName ?? '—'}</Text>
            <Text color="entityDirectory.mutedText" fontSize="12px">
              {inquiry.sent
                ? t('inquiry.sentAt', {
                    date: formatDate(Locale.En, inquiry.sent),
                  })
                : t('inquiry.createdAt', {
                    date: formatDate(Locale.En, inquiry.created),
                  })}
            </Text>
          </Flex>
        );
      },
    }),
  ];

  const detailColumns =
    mode === 'matrix'
      ? [
          columnHelper.display({
            id: 'priceRange',
            header: t('inquiry.priceRange'),
            size: 150,
            minSize: 135,
            cell: ({ row: { original } }) => {
              const { inquiry } = original;
              if (!inquiry) return null;
              const currencyId = displayCurrencyId ?? inquiry.currencyId;
              const currencyCode = findCurrency(currencyId)?.code ?? '';
              const convertPrice = (price: number | null) => {
                if (price == null) return null;
                return displayCurrencyId != null
                  ? convertCurrencyValue(
                      inquiry.currencyId,
                      displayCurrencyId,
                      price,
                      'buy',
                    )
                  : price;
              };
              const minimum = convertPrice(inquiry.minimumPrice);
              const maximum = convertPrice(inquiry.maximumPrice);
              return (
                <Text fontSize="14px" fontWeight="semibold">
                  {minimum == null || maximum == null
                    ? '—'
                    : minimum === maximum
                    ? `${minimum.toFixed(2)} ${currencyCode}`
                    : `${minimum.toFixed(2)}–${maximum.toFixed(
                        2,
                      )} ${currencyCode}`}
                </Text>
              );
            },
          }),
          columnHelper.display({
            id: 'updated',
            header: t('inquiry.updated'),
            size: 120,
            minSize: 110,
            cell: ({ row: { original } }) => {
              const { inquiry } = original;
              if (!inquiry) return null;
              return (
                <Text color="entityDirectory.mutedText" fontSize="12px">
                  {formatDate(
                    Locale.En,
                    inquiry.latestResponse ?? inquiry.sent ?? inquiry.created,
                  )}
                </Text>
              );
            },
          }),
        ]
      : [
          columnHelper.display({
            id: 'mpq',
            header: t('inquiry.mpq'),
            size: 80,
            minSize: 70,
            cell: ({ row: { original } }) => (
              <Text>
                {original.inquiry?.mpqQuantity?.toLocaleString() ?? '—'}
              </Text>
            ),
          }),
          ...tierColumns.map((quantity) =>
            columnHelper.display({
              id: `tier-${quantity}`,
              header: `${quantity.toLocaleString()} ${t('general.pcs')}`,
              size: 110,
              minSize: 100,
              cell: ({ row: { original } }) => {
                const { inquiry } = original;
                if (!inquiry) return null;
                const tier = inquiry.tierPrices.find(
                  (price) => price.quantity === quantity,
                );
                const currencyId = displayCurrencyId ?? inquiry.currencyId;
                const currencyCode = findCurrency(currencyId)?.code ?? '';
                const price =
                  tier?.price != null && displayCurrencyId != null
                    ? convertCurrencyValue(
                        inquiry.currencyId,
                        displayCurrencyId,
                        tier.price,
                        'buy',
                      )
                    : tier?.price;
                const isBelowMpq =
                  inquiry.mpqQuantity != null && quantity < inquiry.mpqQuantity;
                return (
                  <Flex align="center" gap={1} opacity={isBelowMpq ? 0.35 : 1}>
                    {tier?.isMain && (
                      <Icons.Flag
                        boxSize={3}
                        color="entityDirectory.primaryBackground"
                      />
                    )}
                    <Text
                      color={
                        tier?.isMain
                          ? 'entityDirectory.primaryBackground'
                          : 'entityDirectory.text'
                      }
                      fontSize="15px"
                      fontWeight="semibold"
                    >
                      {price != null
                        ? `${price.toFixed(2)} ${currencyCode}`
                        : '—'}
                    </Text>
                  </Flex>
                );
              },
            }),
          ),
        ];

  return [
    ...sharedColumns,
    ...detailColumns,
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 52,
      minSize: 52,
      maxSize: 52,
      meta: { stopCellPropagation: true },
      cell: ({ row: { original } }) => (
        <Flex justify="flex-end">
          {original.inquiry ? (
            <InquiryRowActions inquiry={original.inquiry} />
          ) : (
            <Button
              size="xs"
              variant="texas-solid"
              onClick={() =>
                onActivateInquiry(original.slot.compositionGroupPurchaseInfoId)
              }
            >
              {t('inquiry.active')}
            </Button>
          )}
        </Flex>
      ),
    }),
  ];
}
